import React, { useRef, useState } from 'react'
import { Button, Col, Modal, Row, Tag } from 'antd';
import XLSX from "xlsx";
import helper from '../../utils/helper';
import { getDataSheet, stepperMoving } from '../../stores/actions';
import { useDispatch } from 'react-redux';
import {
    InboxOutlined, ExclamationCircleOutlined, PhoneOutlined, NumberOutlined,
    DollarOutlined, CalendarOutlined, FileTextOutlined, CheckCircleFilled,
} from '@ant-design/icons';
import { prepareHousingIqamaSheet, HousingIqamaServiceType } from './validateRows';

interface ColumnGuide {
    label: string,
    isRequired: boolean,
    description: string,
}

interface Props {
    validCols: ColumnGuide[],
}

const FIELD_ICONS: Record<string, React.ReactNode> = {
    phone: <PhoneOutlined />,
    serviceID: <NumberOutlined />,
    amount: <DollarOutlined />,
    numberOfInstallments: <CalendarOutlined />,
};


const FIELD_EXAMPLES: Record<string, string> = {
    phone: '0701151300',
    serviceID: `${HousingIqamaServiceType.Housing} or ${HousingIqamaServiceType.Iqama}`,
    amount: '15000',
    numberOfInstallments: '6',
};

export default function UploadHousingIqamaSheet(props: Props) {
    const [validFiles, setValidFiles] = useState(false);
    const [viewInstructions, setViewInstructions] = useState(false);
    const [uploadingErrorMessage, setUploadingErrorMessage] = useState('');
    const [uploadingError, setUploadingError] = useState(false);
    const dispatch = useDispatch();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const saveFile = () => {
        const headers = props.validCols.map((column) => column.label);
        const worksheet = XLSX.utils.aoa_to_sheet([headers]);
        const phoneColIndex = headers.indexOf('phone');
        const formattedRows = 500;

        // Pre-format phone as text so Excel keeps the leading 0. Upload ignores fully empty rows.
        if (phoneColIndex >= 0) {
            const colLetter = XLSX.utils.encode_col(phoneColIndex);
            for (let row = 2; row <= formattedRows + 1; row++) {
                worksheet[`${colLetter}${row}`] = { t: 's', v: '', z: '@' };
            }
        }

        worksheet['!ref'] = XLSX.utils.encode_range({
            s: { r: 0, c: 0 },
            e: { r: formattedRows, c: Math.max(headers.length - 1, 0) },
        });
        worksheet['!cols'] = headers.map((label) => ({
            wch: Math.max(label.length + 4, label === 'phone' ? 16 : 22),
        }));

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
        XLSX.writeFile(workbook, 'housing-iqama-bulk-placement.xlsx');
    };

    const forwardClickToInputElement = () => {
        fileInputRef.current?.click();
    };

    const handleFile = (file: any) => {
        setUploadingError(false)
        const file_size = file.size / (1024 * 1024);
        const maxSize = 1;
        const acceptable_exe = helper.regexValidation.xlsxCsvRegex;
        if (!acceptable_exe.exec(file.name)) {
            setValidFiles(true);
        } else if (file_size > maxSize) {
            setValidFiles(true);
        } else {
            setValidFiles(false);
            const fileReader = new FileReader();
            let rowObject: any, result, workbook: XLSX.WorkBook;
            fileReader.readAsBinaryString(file);
            fileReader.onload = (event: any) => {
                result = event.target.result;
                workbook = XLSX.read(result, { type: "binary", cellDates: true });
                workbook.SheetNames.forEach(sheet => {
                    rowObject = helper.sheetToJson(workbook.Sheets[sheet]);
                });

                const requiredHeaders = props.validCols.filter((column) => column.isRequired).map((column) => column.label)
                const parsed = prepareHousingIqamaSheet(rowObject, requiredHeaders)
                if (parsed.error) {
                    setUploadingError(true)
                    setUploadingErrorMessage(parsed.error)
                    return
                }
                dispatch(getDataSheet({ sheetData: parsed.normalizedRows, invalidRows: parsed.invalidRows }))
                dispatch(stepperMoving(1));
            }
        }
    }

    const handleFileChange = (e: { target: { files: any, value: any }; }) => {
        const files = e.target.files;
        if (files && files[0]) handleFile(files[0]);
        e.target.value = null;
    }

    const requiredCount = props.validCols.filter((column) => column.isRequired).length;

    return (
        <>
            <div className="instructions">
                <Row>
                    <Col xl={18} md={18} sm={24}>
                        <p className="head">
                            Before uploading, download the following form to fill in the required data,<br /> then re-upload it to the system
                        </p>
                        <p className='fillData' onClick={() => setViewInstructions(true)} ><ExclamationCircleOutlined /><span style={{ margin: '0 10px' }} className='fillHint'>
                            How to fill in the data sheet
                        </span></p>
                    </Col>
                    <Col xl={6} md={6} sm={24}>
                        <Button className='sample' onClick={saveFile}>Download the file</Button>
                    </Col>
                </Row>
            </div>

            <div className='upload' onClick={forwardClickToInputElement}>
                <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                </p>
                <p className="ant-upload-text">Click to upload the file</p>
                {validFiles && <p className="error-hint">{'Allowed extension CSV or XLS up to 12 MB'}</p>}
            </div>
            <span className="maxSize">The recommended size is up to 1MB</span><br />
            {uploadingError && <span className="maxSize" style={{ color: 'red' }}>{uploadingErrorMessage}</span>}
            <form className="form-inline">
                <div className="form-group">
                    <label htmlFor="file">  </label>
                    <input
                        hidden
                        type="file"
                        className="form-control"
                        id="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        accept='.xlsx,.xls,.csv'
                    />
                </div>
            </form>

            <Modal
                visible={viewInstructions}
                onCancel={() => setViewInstructions(false)}
                centered
                width={560}
                wrapClassName="housing-iqama-guide-modal"
                title={
                    <div className="housing-iqama-guide__title">
                        <span className="housing-iqama-guide__title-icon"><CheckCircleFilled /></span>
                        <span>How to fill the data sheet</span>
                    </div>
                }
                footer={
                    <Button type="primary" className="housing-iqama-guide__ok" onClick={() => setViewInstructions(false)}>
                        Got it
                    </Button>
                }
            >
                <div className="housing-iqama-guide">
                    <p className="housing-iqama-guide__lead">
                        Use the exact column names below. {requiredCount} {requiredCount === 1 ? 'field is' : 'fields are'} required — keep one request per row.
                    </p>
                    <div className="housing-iqama-guide__list">
                        {props.validCols.map((column) => (
                            <div key={column.label} className="housing-iqama-guide__card">
                                <div className="housing-iqama-guide__icon">
                                    {FIELD_ICONS[column.label] || <FileTextOutlined />}
                                </div>
                                <div className="housing-iqama-guide__body">
                                    <div className="housing-iqama-guide__row">
                                        <code className="housing-iqama-guide__name">{column.label}</code>
                                        <Tag
                                            className={column.isRequired ? 'housing-iqama-guide__tag housing-iqama-guide__tag--required' : 'housing-iqama-guide__tag'}
                                        >
                                            {column.isRequired ? 'Required' : 'Optional'}
                                        </Tag>
                                    </div>
                                    <p className="housing-iqama-guide__desc">{column.description}</p>
                                    {FIELD_EXAMPLES[column.label] && (
                                        <span className="housing-iqama-guide__example">
                                            Example: {FIELD_EXAMPLES[column.label]}
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </Modal>
        </>
    )
}
